import { Box, Chip } from '@mui/material'
// import { useCalculateGap } from 'containers/dashboard/dashboardPage/components/recentCompanySearchesCard/hooks/useCalculateGap'
import { groupApplicantsByApplicantCleaned } from 'containers/dashboard/multisectorReport/calculations/calculations'
import { getMultisectorCompetitors } from 'containers/dashboard/multisectorReport/calculations/competitorCalculations'
// import { useMultisectorCompetitors } from 'containers/dashboard/multisectorReport/hooks/useMultisectorCompetitors'
import { GAP_DETAILS } from 'containers/dashboard/multisectorReport/sections/chartsSection/purePlaySection/competitiveStrengthGap/gapTexts'
import { getGapCategory } from 'containers/dashboard/multisectorReport/sections/chartsSection/purePlaySection/gapThresholds'
import { calculateGap } from 'containers/dashboard/multisectorReport/sections/chartsSection/purePlaySection/purePlayCalculations'
import { useState, useEffect } from 'react'
import { authPost, REPORTING_ENDPOINTS } from 'services/api'
import { CompanySearchForMultisectorStatus } from 'store/companySearch/types'
import { Applicant } from 'store/dashboard/types'
import { formatNumber } from 'store/utils'

interface ChipStyleProps {
  label: string | React.ReactNode
  bgcolor: string
  color: string
  fontSize?: number
}

type Props = {
  status: CompanySearchForMultisectorStatus
  multisectorId: string | undefined
}
export default function StatusChip({ status, multisectorId }: Props) {
  const [gapData, setGapData] = useState<{
    gap: number
    category: string
    color: string
  } | null>(null)
  // const [isLoadingMultisector, setIsLoadingMultisector] = useState(false)
  const getGap = (multisectorApplicants: Applicant[]): number => {
    const subgroups = Array.from(new Set(multisectorApplicants.slice(0, 1000).map(item => item.subgroup)))
    const groupedApplicants = groupApplicantsByApplicantCleaned(multisectorApplicants, subgroups)
    const applicantsFirstPerSubgroup = groupedApplicants.map(groupedApp => groupedApp.applicantBySubgroup[0])
    const topCompaniesNumber = 10 // Default value for top companies number
    const targetCompany = applicantsFirstPerSubgroup[0] // Assuming the first company is the target company
    const competitors: Applicant[] = getMultisectorCompetitors(applicantsFirstPerSubgroup, topCompaniesNumber, subgroups)
    const gap = calculateGap(targetCompany, competitors)
    return formatNumber(gap)
  }
  // Default Config
  const getDefaultChipProps = (status: CompanySearchForMultisectorStatus): ChipStyleProps => {
    switch (status) {
      case 'searches_finished':
        return {
          label: 'Completed',
          bgcolor: 'transparent',
          color: 'primary.main',
        }
      case 'initializing':
      case 'fetching_xml':
      case 'winning_patent':
        return {
          label: 'Analyzing',
          bgcolor: 'rgba(255, 193, 7, 0.2)',
          color: '#b26a00',
        }
      case 'searches_queued':
        return {
          label: 'Queue',
          bgcolor: 'rgba(255, 152, 0, 0.2)',
          color: '#bf360c',
        }
      case 'searches_processing':
        return {
          label: 'Processing',
          bgcolor: 'rgba(255, 193, 7, 0.2)',
          color: '#b26a00',
        }
      default:
        return {
          label: status,
          bgcolor: 'grey.200',
          color: 'black',
        }
    }
  }

  let chipProps: ChipStyleProps

  if (status !== 'searches_finished' || !gapData) {
    chipProps = getDefaultChipProps(status)
  } else {
    chipProps = {
      label: (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {gapData.gap}%
          </Box>
          <Box component="span" sx={{ ml: 2.5, width: '80px' }}>
            {gapData.category}
          </Box>
        </Box>
      ),
      bgcolor: 'transparent',
      color: gapData.color,
      fontSize: 15,
    }
  }

  useEffect(() => {
    if (status !== 'searches_finished' || !multisectorId) {
      setGapData(null)
      return
    }

    const fetchMultisectorData = async () => {
      try {
        const response = await authPost(REPORTING_ENDPOINTS.multisectorData(multisectorId), {})
        if (response.status !== 200) {
          setGapData(null)
          return
        }
        const { multisector } = response.data
        const gap = getGap(multisector)
        const category = getGapCategory(gap)
        const gapDetails = GAP_DETAILS[category]

        setGapData({
          gap,
          category: gapDetails.category,
          color: gapDetails.color,
        })
      } catch {
        setGapData(null)
      }
    }
    fetchMultisectorData()
  }, [status, multisectorId])

  return (
    <Chip
      label={chipProps.label}
      sx={{
        fontWeight: 500,
        height: 24,
        fontSize: chipProps.fontSize ?? 13,
        bgcolor: chipProps.bgcolor,
        color: chipProps.color,
      }}
      size="small"
    />
  )
}
